import { useState, useEffect, useRef, useCallback } from 'react';
import { logAuditEvent, saveSprintSnapshot } from '../../../utils/auditLog';
import { shouldAllowEmptyTasksSave } from '../../../utils/allowEmptyTasksSave';
import { migrateSprintEstimatedDuration } from '../../../utils/taskDuration';
import { useUndoRedo } from '../../../hooks/useUndoRedo';
import { getActiveWorkspaceId } from '../../../utils/api/workspaceContext';
import { queuePendingTaskAdds } from '../../../utils/pendingSprintTaskAdds';
import { bindWaitCardEditor } from '../../../utils/waitCardEditor';
import {
  OPTIMISTIC_CAMPAIGN_CARD,
  OPTIMISTIC_CAMPAIGN_CARD_FAILED,
  placeOptimisticCampaignCard,
  removeOptimisticCampaignCard,
} from '../../../hooks/campaignNesting/optimisticCampaignCard';

/**
 * useSprintSync Hook
 * 
 * Manages bidirectional sync between sprint prop and local state.
 * Handles initial load and persisting changes back to parent.
 * Uses debouncing to prevent excessive saves during continuous operations (e.g., resize, drag).
 * 
 * UNDO/REDO:
 * - Uses command-based undo (stores actions, not snapshots)
 * - Actions are recorded via recordAction() which is passed to child hooks
 * - Ctrl+Z / Ctrl+Y support via returned undo/redo functions
 * - History is session-only (clears on refresh)
 * - undoStack/redoStack are exposed for UI display
 * 
 * SAFEGUARDS:
 * - Never sync if incoming sprint has fewer tasks than local (potential data loss)
 * - Comprehensive logging for debugging
 */
export const useSprintSync = (sprint, onUpdateSprint, debounceMs = 500, isMultiCampaignMode = false) => {
  // Local sprint state
  // SAFEGUARD: If sprint has no tasks array, initialize with empty tasks to prevent
  // accidental saves of metadata-only sprints
  const [localSprint, setLocalSprint] = useState(() => {
    if (!sprint) return sprint;
    const base = !Array.isArray(sprint.tasks)
      ? (() => {
          logAuditEvent('INIT_WITH_EMPTY_TASKS', {
            sprintId: sprint.id,
            sprintName: sprint.name,
            reason: 'Sprint prop had no tasks array - initializing with empty array',
          });
          return { ...sprint, tasks: [] };
        })()
      : sprint;
    return migrateSprintEstimatedDuration(base).sprint;
  });
  
  // Undo/redo system (command-based)
  const {
    recordAction,
    undo: undoAction,
    redo: redoAction,
    undoTo,
    redoTo,
    undoAll,
    clearHistory,
    isUndoRedoing,
    startBatch,
    endBatch,
    cancelBatch,
    wrapLastUndoAction,
    undoStack,
    redoStack,
    canUndo,
    canRedo,
  } = useUndoRedo();
  
  // Track last synced sprint to avoid infinite loops
  const lastSyncedRef = useRef({ id: null, tasksLength: 0 });
  
  // Debounce timer ref
  const debounceTimerRef = useRef(null);
  
  // Track the highest task count we've seen for this sprint (safeguard)
  const maxTaskCountRef = useRef({});
  
  // Sync localSprint when sprint prop changes (external data load)
  useEffect(() => {
    const currentId = sprint?.id;
    const currentTasksLength = sprint?.tasks?.length || 0;
    const last = lastSyncedRef.current;
    const localTasksLength = localSprint?.tasks?.length || 0;
    
    const isCampaignSwitch = currentId !== last.id && currentId !== localSprint?.id;

    // SAFEGUARD: If switching to a sprint that has 0 tasks, but we have local data
    // with tasks for that same sprint, DON'T overwrite - the prop is stale
    // (Never apply this across campaign switches — that left the previous campaign on screen.)
    if (!isCampaignSwitch && currentId === localSprint?.id && currentTasksLength === 0 && localTasksLength > 0) {
      logAuditEvent('SYNC_BLOCKED_STALE_PROP', {
        reason: 'Incoming prop has 0 tasks but local has tasks - prop is stale',
        sprintId: currentId,
        sprintName: sprint?.name,
        propTaskCount: currentTasksLength,
        localTaskCount: localTasksLength,
      });
      return; // Don't sync stale data
    }
    
    // SAFEGUARD: If we've seen this sprint with more tasks before, don't accept 0 tasks
    // Skip on campaign switch so we always adopt the newly selected campaign.
    const maxSeenTasks = maxTaskCountRef.current[currentId] || 0;
    if (!isCampaignSwitch && currentTasksLength === 0 && maxSeenTasks > 0) {
      logAuditEvent('SYNC_BLOCKED_ZERO_TASKS', {
        reason: 'Incoming prop has 0 tasks but we previously saw tasks for this sprint',
        sprintId: currentId,
        sprintName: sprint?.name,
        propTaskCount: currentTasksLength,
        maxSeenTasks: maxSeenTasks,
      });
      return; // Don't sync - this would wipe data
    }
    
    // Only sync if ID changed OR if tasks were loaded (went from 0 to >0)
    if (currentId !== last.id || (currentTasksLength > 0 && last.tasksLength === 0)) {
      logAuditEvent('SYNC_FROM_PROP', {
        reason: currentId !== last.id ? 'ID_CHANGED' : 'TASKS_LOADED',
        sprintId: currentId,
        sprintName: sprint?.name,
        prevTaskCount: last.tasksLength,
        newTaskCount: currentTasksLength,
      }, sprint);
      
      lastSyncedRef.current = { id: currentId, tasksLength: currentTasksLength };
      
      // Update max task count tracker
      if (currentId && currentTasksLength > 0) {
        maxTaskCountRef.current[currentId] = Math.max(
          maxTaskCountRef.current[currentId] || 0,
          currentTasksLength
        );
      }
      
      // Set state directly (not undoable - this is loading data)
      const { sprint: normalizedSprint } = migrateSprintEstimatedDuration(sprint);
      setLocalSprint(normalizedSprint);
      
      // Clear undo history when switching campaigns
      if (currentId !== last.id) {
        clearHistory();
      }
    }
  }, [sprint?.id, sprint?.tasks?.length, sprint, localSprint?.id, localSprint?.tasks?.length, clearHistory]);
  
  // NOTE: We intentionally do NOT sync when sprint prop changes for things like nestedSprintId
  // This was causing data loss because the global sprint might have stale task data

  // Sync name and icon changes from prop to localSprint (real-time updates from sidebar)
  // This allows name/icon changes made in the sidebar to immediately reflect in the open view
  useEffect(() => {
    // Only sync if we're viewing the same sprint (ID matches)
    if (!sprint || !localSprint || sprint.id !== localSprint.id) return;
    
    // Check if name or icon changed
    const nameChanged = sprint.name !== localSprint.name;
    const iconChanged = sprint.icon !== localSprint.icon;
    
    if (nameChanged || iconChanged) {
      logAuditEvent('SYNC_METADATA_FROM_PROP', {
        sprintId: sprint.id,
        nameChanged,
        iconChanged,
        oldName: localSprint.name,
        newName: sprint.name,
        oldIcon: localSprint.icon,
        newIcon: sprint.icon,
      });
      
      // Update only the name and icon, preserving all other local state
      setLocalSprint(prev => ({
        ...prev,
        name: sprint.name,
        icon: sprint.icon,
      }));
    }
  }, [sprint?.id, sprint?.name, sprint?.icon, localSprint?.id, localSprint?.name, localSprint?.icon]);

  // Sync local changes back to parent for persistence (debounced)
  const prevLocalSprintRef = useRef(null);
  const waitEditorSprintRef = useRef(localSprint);
  waitEditorSprintRef.current = localSprint;
  const pendingSyncRef = useRef(null);
  
  useEffect(() => {
    if (!localSprint || !onUpdateSprint) return;
    
    // Skip the initial sync
    if (prevLocalSprintRef.current === null) {
      prevLocalSprintRef.current = localSprint;
      return;
    }
    
    // Skip if same object reference
    if (prevLocalSprintRef.current === localSprint) return;
    
    // IMPORTANT: If sprint ID changed, we're switching campaigns - reset the ref and skip
    // This prevents false "all tasks deleted" warnings when switching from a campaign
    // with tasks to one that hasn't loaded yet
    if (prevLocalSprintRef.current.id !== localSprint.id) {
      if (pendingSyncRef.current) {
        // A campaign switch can happen before the debounce fires. Flush the pending
        // local edit so quick drag-then-switch interactions do not lose positions.
        const pendingSprint = pendingSyncRef.current;
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        onUpdateSprint(pendingSprint);
        pendingSyncRef.current = null;
      }
      prevLocalSprintRef.current = localSprint;
      return; // Don't sync or warn - this is a campaign switch, not a data change
    }

    // A (+) campaign card is on screen before the nested campaign exists.
    // Keep it local until nesting stamps the real id, so a save cannot
    // overwrite that link with the unfinished card.
    if ((localSprint.tasks || []).some((task) => task?.creatingCampaign)) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingSyncRef.current = null;
      prevLocalSprintRef.current = localSprint;
      return;
    }
    
    const prevTaskCount = prevLocalSprintRef.current?.tasks?.length || 0;
    const newTaskCount = localSprint.tasks?.length || 0;
    
    // CRITICAL SAFEGUARD: Block sync that would delete all tasks
    // This catches bugs before they reach the save layer.
    // Exception: user confirmed Ctrl+A + Delete (see allowEmptyTasksSave.js).
    const allowEmptySync = newTaskCount === 0 && shouldAllowEmptyTasksSave(localSprint.id);
    if (newTaskCount === 0 && prevTaskCount > 0 && !allowEmptySync) {
      logAuditEvent('SYNC_BLOCKED_ALL_TASKS_DELETED', {
        sprintId: localSprint.id,
        sprintName: localSprint.name,
        prevTaskCount,
        newTaskCount,
        reason: 'Blocked sync that would delete all tasks - likely a bug',
      }, prevLocalSprintRef.current); // Save snapshot of PREVIOUS state for recovery
      
      console.error(
        `🛑 BLOCKED SYNC: "${localSprint.name}" went from ${prevTaskCount} tasks to 0. ` +
        `This is likely a bug. NOT syncing to prevent data loss.`
      );
      return; // DON'T SYNC - this would wipe all data
    }

    if (allowEmptySync) {
      maxTaskCountRef.current[localSprint.id] = 0;
      logAuditEvent('INTENTIONAL_EMPTY_CAMPAIGN_SYNC', {
        sprintId: localSprint.id,
        sprintName: localSprint.name,
        prevTaskCount,
      });
    }

    // New missions must land in the room immediately — do not wait for
    // debounce + save throttle, or a refresh loses them.
    if (newTaskCount > prevTaskCount) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingSyncRef.current = null;
      const prevIds = new Set((prevLocalSprintRef.current?.tasks || []).map((t) => t.id));
      const added = (localSprint.tasks || []).filter((t) => t?.id && !prevIds.has(t.id));
      queuePendingTaskAdds(getActiveWorkspaceId(), localSprint.id, added);
      prevLocalSprintRef.current = localSprint;
      onUpdateSprint(localSprint, { immediate: true });
      return;
    }
    
    // LOG task count decreases for debugging
    if (newTaskCount < prevTaskCount && prevTaskCount > 0) {
      const tasksLost = prevTaskCount - newTaskCount;
      logAuditEvent('TASK_COUNT_DECREASED', {
        sprintId: localSprint.id,
        sprintName: localSprint.name,
        prevTaskCount,
        newTaskCount,
        tasksLost,
      }, prevLocalSprintRef.current); // Save snapshot of PREVIOUS state for recovery
    }
    
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce the sync to parent - prevents excessive saves during resize/drag
    pendingSyncRef.current = localSprint;
    debounceTimerRef.current = setTimeout(() => {
      logAuditEvent('SAVE_TO_PARENT', {
        sprintId: localSprint.id,
        sprintName: localSprint.name,
        taskCount: localSprint.tasks?.length || 0,
      }, localSprint); // Save snapshot for recovery
      
      onUpdateSprint(localSprint);
      prevLocalSprintRef.current = localSprint;
      pendingSyncRef.current = null;
      debounceTimerRef.current = null;
    }, debounceMs);
    
    // Cleanup on unmount or before next effect
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [localSprint, onUpdateSprint, debounceMs]);
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (pendingSyncRef.current && onUpdateSprint) {
        const pendingSprint = pendingSyncRef.current;
        clearTimeout(debounceTimerRef.current);
        onUpdateSprint(pendingSprint);
        pendingSyncRef.current = null;
        debounceTimerRef.current = null;
      }
    };
  }, [onUpdateSprint]);

  // Listen for campaign nesting events to update localSprint's parentSprintId
  // This prevents the localSprint from overwriting the parentSprintId when it syncs
  useEffect(() => {
    const handleCampaignNested = (event) => {
      const { draggedSprintId, newParentSprintId } = event.detail;
      
      // Only handle if this is the sprint we're viewing
      if (draggedSprintId === localSprint?.id) {
        setLocalSprint(prev => {
          if (!prev || prev.id !== draggedSprintId) return prev;
          return {
            ...prev,
            parentSprintId: newParentSprintId,
          };
        });
      }
    };
    
    window.addEventListener('campaignNested', handleCampaignNested);
    return () => window.removeEventListener('campaignNested', handleCampaignNested);
  }, [localSprint?.id]);

  useEffect(() => {
    const apply = (taskId, patch, campaignId) => {
      const current = waitEditorSprintRef.current;
      if (!current?.tasks?.some((task) => task.id === taskId)) return false;
      if (campaignId && current.id !== campaignId) return false;
      const updatedAt = new Date().toISOString();
      setLocalSprint((prev) => {
        if (!prev?.tasks) return prev;
        if (campaignId && prev.id !== campaignId) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map((task) => (
            task.id === taskId ? { ...task, ...patch, updatedAt } : task
          )),
        };
      });
      return true;
    };
    return bindWaitCardEditor(apply);
  }, [setLocalSprint]);

  // Listen for sprint tasks updated events (when tasks are added/removed via sidebar drag)
  // CRITICAL: This prevents race conditions where localSprint would overwrite API changes
  useEffect(() => {
    const cancelPendingDebounce = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingSyncRef.current = null;
    };

    const handleSprintTasksUpdated = (event) => {
      const { sprintId, tasks, nodePositions } = event.detail;

      console.log('[M2S] sprintTasksUpdated event received by view', {
        eventSprintId: sprintId,
        viewingSprintId: localSprint?.id,
        matchesView: sprintId === localSprint?.id,
        isMultiCampaignMode,
        newTaskCount: tasks?.length,
      });

      if (sprintId === localSprint?.id) {
        cancelPendingDebounce();
        setLocalSprint(prev => {
          if (!prev || prev.id !== sprintId) return prev;
          const localById = new Map((prev.tasks || []).map((task) => [task.id, task]));
          const mergedTasks = (tasks || []).map((task) => {
            const local = localById.get(task.id);
            // Tidy may already have moved the card while its campaign was spinning.
            if (!local?.creatingCampaign) return task;
            return { ...task, x: local.x, y: local.y };
          });
          const mergedPositions = { ...(nodePositions || prev.nodePositions || {}) };
          mergedTasks.forEach((task) => {
            if (localById.get(task.id)?.creatingCampaign && Number.isFinite(task.x) && Number.isFinite(task.y)) {
              mergedPositions[task.id] = { x: task.x, y: task.y };
            }
          });
          return {
            ...prev,
            tasks: mergedTasks,
            ...(nodePositions || mergedTasks.some((task) => localById.get(task.id)?.creatingCampaign)
              ? { nodePositions: mergedPositions }
              : {}),
          };
        });
        return;
      }

      // Multi-campaign view: remove missions that left a source campaign
      if (isMultiCampaignMode && localSprint?.id?.startsWith('view-')) {
        const sourceTaskIds = new Set((tasks || []).map((t) => t.id));
        setLocalSprint((prev) => {
          if (!prev) return prev;
          const filtered = prev.tasks.filter((t) => {
            if (t.sourceCampaign?.id !== sprintId) return true;
            return sourceTaskIds.has(t.id);
          });
          if (filtered.length === prev.tasks.length) return prev;
          cancelPendingDebounce();
          return { ...prev, tasks: filtered };
        });
      }
    };

    const handleCancelDebounce = (event) => {
      const { sprintId } = event.detail;
      if (sprintId === localSprint?.id) {
        cancelPendingDebounce();
      }
    };

    const tidyGraph = () => {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('flowchartTidyGraph'));
      }, 50);
    };

    const handleOptimisticCampaignCard = (event) => {
      const detail = event.detail || {};
      if (!detail.sprintId || detail.sprintId !== localSprint?.id || isMultiCampaignMode) return;
      setLocalSprint((prev) => placeOptimisticCampaignCard(prev, detail));
      tidyGraph();
    };

    const handleOptimisticCampaignCardFailed = (event) => {
      const { sprintId, taskId } = event.detail || {};
      if (!taskId || (sprintId && sprintId !== localSprint?.id)) return;
      setLocalSprint((prev) => removeOptimisticCampaignCard(prev, taskId));
      tidyGraph();
    };

    window.addEventListener('sprintTasksUpdated', handleSprintTasksUpdated);
    window.addEventListener('sprintSyncDebounceCancelled', handleCancelDebounce);
    window.addEventListener(OPTIMISTIC_CAMPAIGN_CARD, handleOptimisticCampaignCard);
    window.addEventListener(OPTIMISTIC_CAMPAIGN_CARD_FAILED, handleOptimisticCampaignCardFailed);
    return () => {
      window.removeEventListener('sprintTasksUpdated', handleSprintTasksUpdated);
      window.removeEventListener('sprintSyncDebounceCancelled', handleCancelDebounce);
      window.removeEventListener(OPTIMISTIC_CAMPAIGN_CARD, handleOptimisticCampaignCard);
      window.removeEventListener(OPTIMISTIC_CAMPAIGN_CARD_FAILED, handleOptimisticCampaignCardFailed);
    };
  }, [localSprint?.id, isMultiCampaignMode]);

  // Wrap undo to work with our state
  const undo = useCallback(() => {
    return undoAction();
  }, [undoAction]);

  const redo = useCallback(() => {
    return redoAction();
  }, [redoAction]);

  return {
    localSprint,
    setLocalSprint,
    // Undo/redo
    recordAction,
    undo,
    redo,
    undoTo,
    redoTo,
    undoAll,
    clearHistory,
    isUndoRedoing,
    // Batch operations
    startBatch,
    endBatch,
    cancelBatch,
    wrapLastUndoAction,
    // History stacks for UI display
    undoStack,
    redoStack,
    canUndo,
    canRedo,
  };
};

export default useSprintSync;
